import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { ArrowLeft, FileText, Building2, Shield, Users, ExternalLink, AlertCircle, GraduationCap } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

export const TermsOfService = () => {
  const navigate = useNavigate();

  const registrationNumbers = [
    { label: "SEBI Registration No.", value: "INZ000279531" },
    { label: "PMS SEBI Registration No.", value: "INP000006396" },
    { label: "CDSL DP", value: "IN-DP-CDSL-43-2015" },
    { label: "MCX", value: "10565" },
    { label: "FMC", value: "TCM CORPO161" },
    { label: "NSE", value: "7520" },
    { label: "BSE", value: "6370" }
  ];

  const importantLinks = [
    { name: "BSE", url: "https://www.bseindia.com/" },
    { name: "NSE", url: "https://www.nseindia.com/" },
    { name: "MCX", url: "https://www.mcxindia.com/" },
    { name: "SEBI", url: "https://www.sebi.gov.in/" },
    { name: "SEBI Investor", url: "https://investor.sebi.gov.in/" },
    { name: "SCORES Portal", url: "https://scores.sebi.gov.in/" },
    { name: "BSE Investor Protection Fund", url: "https://www.bseipf.com/investors_education.html" }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-green-50">
      {/* Header */}
      <div className="bg-white border-b border-slate-200 sticky top-0 z-50">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <Button 
              variant="ghost"
              onClick={() => navigate('/')}
              className="flex items-center text-blue-600 hover:text-blue-700"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to IndexMaster
            </Button>
            <div className="flex items-center space-x-2">
              <FileText className="w-6 h-6 text-blue-600" />
              <span className="font-semibold text-slate-800">Terms of Service</span>
            </div>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <Card className="bg-white/80 backdrop-blur-sm shadow-xl border-blue-100 mb-8">
          <CardHeader className="text-center">
            <CardTitle className="text-3xl font-bold text-slate-800 mb-4">
              Terms of Service
            </CardTitle>
            <p className="text-slate-600">
              Composite Investments Pvt Ltd (IndexMaster Investment Approach)
            </p>
            <p className="text-sm text-slate-500 mt-2">
              Last updated: {new Date().toLocaleDateString()}
            </p>
          </CardHeader>
        </Card>

        <div className="grid lg:grid-cols-3 gap-8">
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-6">
            {/* Company Information */}
            <Card className="bg-white/80 backdrop-blur-sm shadow-lg border-blue-100">
              <CardHeader>
                <CardTitle className="flex items-center text-xl font-bold text-slate-800">
                  <Building2 className="w-5 h-5 mr-2 text-blue-600" />
                  Company Registration & Regulatory Information
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-slate-700 mb-4">
                  Composite Investments Pvt Ltd is a registered member of SEBI with multiple regulatory registrations:
                </p>
                <div className="grid md:grid-cols-2 gap-3">
                  {registrationNumbers.map((reg, index) => (
                    <div key={index} className="flex justify-between items-center bg-slate-50 p-3 rounded-lg">
                      <span className="text-sm font-medium text-slate-700">{reg.label}:</span>
                      <Badge variant="secondary" className="bg-blue-100 text-blue-800 font-mono">
                        {reg.value}
                      </Badge>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>

            {/* Investor Advisory */}
            <Card className="bg-white/80 backdrop-blur-sm shadow-lg border-red-100">
              <CardHeader>
                <CardTitle className="flex items-center text-xl font-bold text-slate-800">
                  <AlertCircle className="w-5 h-5 mr-2 text-red-600" />
                  Important Investor Advisory & Warnings
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-4">
                  <p className="text-red-800 font-semibold mb-2">⚠️ Prohibited Schemes</p>
                  <p className="text-slate-700">
                    Investors are strongly advised <strong>NOT</strong> to subscribe to any scheme or products/arrangements offered by any entities and persons involving assured/guaranteed returns or unauthorized portfolio management/collective investment schemes.
                  </p>
                </div>
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                  <p className="text-amber-800 font-semibold mb-2">📋 Regulatory Compliance</p>
                  <p className="text-slate-700">
                    Such arrangements/schemes are <strong>NOT PERMITTED</strong> under the SEBI/NSE Bye-Law Rules and Regulations.
                  </p>
                </div>
              </CardContent>
            </Card>

            {/* KYC Information */}
            <Card className="bg-white/80 backdrop-blur-sm shadow-lg border-green-100">
              <CardHeader>
                <CardTitle className="flex items-center text-xl font-bold text-slate-800">
                  <Shield className="w-5 h-5 mr-2 text-green-600" />
                  Know Your Customer (KYC)
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                  <p className="text-green-800 font-semibold mb-2">✅ One-Time Process</p>
                  <p className="text-slate-700 mb-3">
                    KYC is a one-time exercise when dealing in the securities market.
                  </p>
                  <p className="text-slate-700">
                    <strong>Intermediary Portability:</strong> Once your KYC is completed through a SEBI registered intermediary (e.g., Broker, DP, Mutual Fund), you need not undergo the same process again when approaching another intermediary.
                  </p>
                </div>
              </CardContent>
            </Card>

            {/* Account Management */}
            <Card className="bg-white/80 backdrop-blur-sm shadow-lg border-blue-100">
              <CardHeader>
                <CardTitle className="flex items-center text-xl font-bold text-slate-800">
                  <Users className="w-5 h-5 mr-2 text-blue-600" />
                  Account Management & Updates
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-slate-700 mb-4">
                  Kindly contact <strong>help@compositeinvestments.com</strong> to update/add/modify the following details:
                </p>
                <div className="grid md:grid-cols-2 gap-3">
                  <div className="flex items-center space-x-2">
                    <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
                    <span className="text-slate-700">Bank account details</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                    <span className="text-slate-700">Demat account details</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
                    <span className="text-slate-700">Email ID</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                    <span className="text-slate-700">Mobile number</span>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Investor Education */}
            <Card className="bg-white/80 backdrop-blur-sm shadow-lg border-purple-100">
              <CardHeader>
                <CardTitle className="flex items-center text-xl font-bold text-slate-800">
                  <GraduationCap className="w-5 h-5 mr-2 text-purple-600" />
                  Investor Awareness and Education
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="bg-purple-50 border border-purple-200 rounded-lg p-4 mb-4">
                  <p className="text-purple-800 font-semibold mb-2">💡 Stay Informed</p>
                  <p className="text-slate-700 mb-3">
                    In the rapidly evolving dynamics of financial markets, it is crucial for investors to remain updated and well-informed about various aspects of investing in the securities market.
                  </p>
                  <p className="text-lg font-semibold text-purple-700 text-center">
                    "We believe that an educated investor is a protected investor!!!"
                  </p>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* PMS Specifics */}
            <Card className="bg-white/80 backdrop-blur-sm shadow-lg border-blue-100">
              <CardHeader>
                <CardTitle className="text-lg font-bold text-slate-800">
                  Portfolio Management Services (PMS)
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="bg-blue-50 rounded-lg p-4">
                  <h4 className="font-semibold text-blue-800 mb-2">Minimum Investment</h4>
                  <p className="text-slate-700 text-2xl font-bold">₹50 Lakhs</p>
                  <p className="text-sm text-slate-600">per customer (SEBI regulations)</p>
                </div>
                <div className="bg-green-50 rounded-lg p-4">
                  <h4 className="font-semibold text-green-800 mb-2">Investment Horizon</h4>
                  <p className="text-slate-700">No mandatory minimum period</p>
                  <p className="text-sm text-green-700 font-medium">Recommended: 2-3 years</p>
                </div>
                <div className="bg-purple-50 rounded-lg p-4">
                  <h4 className="font-semibold text-purple-800 mb-2">NRI Eligibility</h4>
                  <p className="text-slate-700">✅ NRI/Foreign Nationals can avail PMS</p>
                </div>
              </CardContent>
            </Card>

            {/* Grievances */}
            <Card className="bg-white/80 backdrop-blur-sm shadow-lg border-orange-100">
              <CardHeader>
                <CardTitle className="text-lg font-bold text-slate-800">
                  Grievances Redressal
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  <div className="bg-orange-50 rounded-lg p-3">
                    <h4 className="font-semibold text-orange-800 mb-2">SCORES Portal</h4>
                    <p className="text-sm text-slate-700 mb-2">File complaints easily & quickly</p>
                    <Button 
                      size="sm"
                      className="w-full bg-orange-600 hover:bg-orange-700"
                      onClick={() => window.open('https://scores.sebi.gov.in/', '_blank')}
                    >
                      Visit SCORES
                      <ExternalLink className="w-3 h-3 ml-2" />
                    </Button>
                  </div>
                  <div className="text-sm text-slate-600">
                    <p className="font-medium mb-1">Required Details:</p>
                    <ul className="space-y-1 text-xs">
                      <li>• Name & PAN</li>
                      <li>• Address & Mobile</li>
                      <li>• Email ID</li>
                    </ul>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Important Links */}
            <Card className="bg-white/80 backdrop-blur-sm shadow-lg border-slate-100">
              <CardHeader>
                <CardTitle className="text-lg font-bold text-slate-800">
                  Important Links
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  {importantLinks.map((link, index) => (
                    <Button 
                      key={index}
                      variant="outline"
                      size="sm"
                      className="w-full justify-between text-left"
                      onClick={() => window.open(link.url, '_blank')}
                    >
                      {link.name}
                      <ExternalLink className="w-3 h-3" />
                    </Button>
                  ))}
                </div>
              </CardContent>
            </Card>
          </div>
        </div>

        {/* Contact Information */}
        <Card className="bg-gradient-to-r from-blue-50 to-green-50 border-blue-200 shadow-xl mt-8">
          <CardContent className="text-center py-8">
            <h3 className="text-xl font-bold text-slate-800 mb-4">Need Help or Have Questions?</h3>
            <p className="text-slate-600 mb-6">
              For any enquiries or further information, please fill our enquiry form or reach out to us directly.
            </p>
            <div className="flex flex-col sm:flex-row justify-center items-center space-y-2 sm:space-y-0 sm:space-x-6">
              <div className="text-sm text-slate-600">
                <p><strong>Email:</strong> help@compositeinvestments.com</p>
              </div>
              <div className="text-sm text-slate-600">
                <p><strong>Phone:</strong> +91 98802 72472</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};